import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  ArrowRight,
  TrendingUp,
  Star,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

const GRADE_COLOR = {
  A: "#5C8D73",
  B: "#547A8F",
  C: "#D18E63",
  D: "#C66B59",
};

export default function StocksToWatch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    let cancel = false;
    api
      .get("/dashboard/recommendations")
      .then((r) => {
        if (!cancel) setData(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, []);

  const addToWatchlist = async (item) => {
    setAdding(item.symbol);
    try {
      // Find user's first watchlist (or rely on the backend's default fallback)
      const lists = await api.get("/watchlists");
      const target = lists.data.watchlists?.[0];
      await api.post("/watchlist", {
        symbol: item.symbol,
        name: item.name,
        asset_type: "stock",
        watchlist_id: target?.id,
      });
      toast.success(`${item.symbol} added to ${target?.name || "your watchlist"}`);
      // Remove from local list
      setData((d) => ({
        ...d,
        items: d.items.filter((i) => i.symbol !== item.symbol),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add to watchlist");
    } finally {
      setAdding(null);
    }
  };

  if (loading) {
    return (
      <div
        className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]"
        data-testid="stocks-to-watch-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Looking for ideas matching your watchlist...
      </div>
    );
  }

  if (!data || !data.items || data.items.length === 0) {
    if (data?.reason === "watchlist_empty") {
      return null; // hide silently if watchlist is empty
    }
    return null;
  }

  return (
    <div
      className="rounded-2xl bg-white border border-[#EAE6E1] p-6 relative overflow-hidden"
      data-testid="stocks-to-watch"
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#D18E63] to-[#547A8F]" />
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-[#D18E63]" strokeWidth={1.75} />
            UpTick picks
          </div>
          <h2 className="font-heading text-2xl tracking-tight text-[#2A2F2B]">
            Stocks you should watch
          </h2>
          <p className="text-sm text-[#6B7068] mt-0.5 leading-relaxed max-w-xl">
            {data.rationale}
          </p>
        </div>
      </div>

      <div className="space-y-2 mt-5" data-testid="stocks-to-watch-list">
        {data.items.map((it, idx) => (
          <div
            key={it.symbol}
            data-testid={`stocks-to-watch-row-${it.symbol}`}
            className="flex items-center gap-3 p-3 rounded-xl border border-[#EAE6E1] bg-[#F9F8F6] hover:bg-white hover:border-[#D18E63]/40 transition-soft group"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-heading text-lg font-bold text-white"
              style={{ backgroundColor: GRADE_COLOR[it.grade] || "#6B7068" }}
            >
              {it.grade}
            </div>
            <div className="flex-1 min-w-0">
              <Link
                to={`/asset/stock/${it.symbol}`}
                className="font-mono-ticker text-sm font-semibold text-[#2A2F2B] hover:text-[#547A8F]"
              >
                {it.symbol}
              </Link>
              <span className="text-xs text-[#6B7068] ml-2 truncate">{it.name}</span>
              <div className="text-[11px] text-[#6B7068] mt-0.5 font-mono-ticker">
                {it.sector} · {it.market_cap} · CAN SLIM {it.total}/{it.max} ({it.percent}%)
              </div>
            </div>
            <div className="text-right">
              {it.change_pct != null && (
                <div className="font-mono-ticker text-sm font-semibold text-[#5C8D73] flex items-center justify-end gap-0.5">
                  <TrendingUp className="w-3 h-3" strokeWidth={2.5} />
                  {it.change_pct.toFixed(2)}%
                </div>
              )}
              {it.price != null && (
                <div className="font-mono-ticker text-xs text-[#6B7068]">
                  ${it.price.toFixed(2)}
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => addToWatchlist(it)}
              disabled={adding === it.symbol}
              data-testid={`stocks-to-watch-add-${it.symbol}`}
              className="rounded-full bg-white border-[#EAE6E1] hover:border-[#D18E63] text-[#2A2F2B] text-xs"
            >
              {adding === it.symbol ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <Star className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Add
                </>
              )}
            </Button>
            <Link
              to={`/asset/stock/${it.symbol}`}
              data-testid={`stocks-to-watch-view-${it.symbol}`}
              className="text-[#6B7068] hover:text-[#2A2F2B] opacity-0 group-hover:opacity-100 transition-soft p-1"
              aria-label="View asset"
            >
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        ))}
        <p className="text-[10px] text-[#6B7068] mt-3 pt-2 border-t border-[#EAE6E1]">
          Educational picks based on your watchlist's sector tilt + CAN SLIM scoring. Not investment advice.
        </p>
      </div>
    </div>
  );
}
